import {
  CBadge,
  CButton,
  CCol,
  CFormInput,
  CFormLabel,
  CLoadingButton,
  CRow,
} from '@coreui/react-pro'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import PlayerAvailabilityService from 'src/service/PlayerAvailabilityService'
import ToastComponent from 'src/components/common/TaostComponent'

const AvailabilityTab = ({ playerId, onCancel, onToggleStatus, isActive }) => {
  const [periods, setPeriods] = useState([])
  const [loading, setLoading] = useState(false)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTill, setDateTill] = useState('')
  const [reason, setReason] = useState('')
  const [adding, setAdding] = useState(false)

  const loadPeriods = () => {
    setLoading(true)
    const params = new URLSearchParams({ player_id: playerId })
    PlayerAvailabilityService.getAvailability(0, 50, 1, params).then((res) => {
      setPeriods(res.data?.data || [])
      setLoading(false)
    })
  }

  useEffect(() => {
    loadPeriods()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId])

  const today = moment().format('YYYY-MM-DD')
  const currentPeriod = periods.find((p) => p.date_from <= today && p.date_till >= today)

  const isOverlapping = (fromA, tillA, fromB, tillB) => fromA <= tillB && tillA >= fromB

  // Native date inputs hand back "YYYY-MM-DD", which is exactly what the API expects.
  const handleDateFrom = (e) => {
    const value = e.target.value
    setDateFrom(value)
    // Keep an until date that still falls after the new start; otherwise make them pick again.
    if (dateTill && value && dateTill < value) {
      setDateTill('')
    }
  }

  const handleAdd = () => {
    if (!dateFrom || !dateTill) {
      ToastComponent('Please select both dates', 'error')
      return
    }
    if (dateFrom > dateTill) {
      ToastComponent('"Unavailable From" date must be before "Unavailable Until" date', 'error')
      return
    }
    const overlappingPeriod = periods.find((p) =>
      isOverlapping(dateFrom, dateTill, p.date_from, p.date_till),
    )
    if (overlappingPeriod) {
      ToastComponent(
        `This date range overlaps with an existing unavailable period (${moment(
          overlappingPeriod.date_from,
        ).format('D MMM YYYY')} – ${moment(overlappingPeriod.date_till).format('D MMM YYYY')})`,
        'error',
      )
      return
    }
    setAdding(true)
    PlayerAvailabilityService.saveAvailability({
      player: [playerId],
      date_from: dateFrom,
      date_till: dateTill,
      reason,
    })
      .then((res) => {
        if (res.status === 200) {
          ToastComponent(res.message, 'success')
          setDateFrom('')
          setDateTill('')
          setReason('')
          loadPeriods()
        } else {
          ToastComponent(res.message, 'error')
        }
        setAdding(false)
      })
      .catch(() => {
        ToastComponent('Something went wrong', 'error')
        setAdding(false)
      })
  }

  const handleRemove = (id) => {
    PlayerAvailabilityService.deleteAvailability({ id }).then((res) => {
      if (res.status === 200) {
        ToastComponent('Unavailable period removed.', 'success')
        loadPeriods()
      }
    })
  }

  return (
    <div>
      <div className="fw-bold mb-2">Current Availability</div>
      {currentPeriod ? (
        <>
          <CBadge color="warning">Unavailable</CBadge>
          <div className="text-medium-emphasis small mt-1">
            Until {moment(currentPeriod.date_till).format('D MMM YYYY')}
          </div>
        </>
      ) : (
        <>
          <CBadge color="success">Available</CBadge>
          <div className="text-medium-emphasis small mt-1">Available for selection</div>
        </>
      )}

      <hr className="my-4" />

      <div className="fw-bold mb-3">Schedule Unavailability</div>
      <CRow className="g-3">
        <CCol md={6}>
          <CFormLabel htmlFor="unavailable-from">Unavailable From</CFormLabel>
          <CFormInput
            type="date"
            id="unavailable-from"
            value={dateFrom}
            onChange={handleDateFrom}
          />
        </CCol>
        <CCol md={6}>
          <CFormLabel htmlFor="unavailable-until">Unavailable Until</CFormLabel>
          <CFormInput
            type="date"
            id="unavailable-until"
            min={dateFrom || undefined}
            value={dateTill}
            onChange={(e) => setDateTill(e.target.value)}
          />
        </CCol>
        <CCol md={12}>
          <CFormLabel htmlFor="reason">Reason</CFormLabel>
          <CFormInput
            id="reason"
            placeholder="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </CCol>
        <CCol md={12}>
          <CLoadingButton
            type="button"
            color="primary"
            variant="outline"
            loading={adding}
            onClick={handleAdd}
          >
            Add Unavailable Period
          </CLoadingButton>
        </CCol>
      </CRow>

      <hr className="my-4" />

      <div className="fw-bold mb-2">Scheduled Periods</div>
      {!loading && periods.length === 0 && (
        <div className="text-medium-emphasis small">No unavailable periods scheduled.</div>
      )}
      {periods.map((period) => (
        <div
          key={period.id}
          className="d-flex align-items-center justify-content-between border rounded-3 p-2 mb-2"
        >
          <div>
            <div className="small fw-medium">
              {moment(period.date_from).format('D MMM YYYY')} –{' '}
              {moment(period.date_till).format('D MMM YYYY')}
            </div>
            {period.reason && <div className="text-medium-emphasis small">{period.reason}</div>}
          </div>
          <CButton color="danger" variant="ghost" size="sm" onClick={() => handleRemove(period.id)}>
            Remove
          </CButton>
        </div>
      ))}

      <div className="d-flex justify-content-between align-items-center mt-4">
        <CButton
          color="danger"
          variant="ghost"
          type="button"
          onClick={() => onToggleStatus(isActive ? 0 : 1)}
        >
          {isActive ? 'Deactivate' : 'Activate'}
        </CButton>
        <CButton color="secondary" variant="outline" type="button" onClick={onCancel}>
          Close
        </CButton>
      </div>
    </div>
  )
}

export default AvailabilityTab
